import { createDefaultLogger, LogLevels } from "@/types/console-logger";
const logger = createDefaultLogger( LogLevels.DEBUG );

import { goFetch } from "@/utils/network-utils";

import { cache, sectorHistory, syncState } from "@/services/cache";
import { sectorHistorySyncKey } from "@/types/products/product-keys";

import {
    BpiStatus,
    NormalizedPersistedSectorsState,
    NormalizedSectorMarketState,
    SectorAlert,
    SectorMarketState
} from "@tmi/types/entities";
import { CompositionContext } from "@/services/data/sync-supervisor";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";
import { DateTime } from "luxon";
import {
    SectorAlertApiResponse,
    SectorHistoryApiResponse,
    SectorHistoryForDate,
    SectorStatsApiResponse
} from "@tmi/types/api/market-data";
import { SectorAlertTypes } from "@tmi/types/enums/sector-alerts";
import { Tuple } from "@tmi/types/adts";
import { normalizeColumn, normalizeSignal } from "@/services/data/sync/normalize-column";

type SectorHistoryMetaApiResponse = {
    sectorHistoryMetaData: {
        dts: string[]
    }
}

async function fetchSectorHistoryKeys(): Promise< string[] > {
    let allSectorHistoryDates: string[] = [];

    // Timing - testing to see how expensive each method is
    const t0: number = performance.now();

    let sectorHistoryMetaPayload: string[] = [];
    const response =
        await goFetch< SectorHistoryMetaApiResponse, PersistedQueryPayload >( {
            serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
            endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
            payload: {
                id: 'd271528e71bf59d763917ba4acd14273'
            }
        } );

    if ( response?.data?.sectorHistoryMetaData?.dts ) {
        sectorHistoryMetaPayload = response.data.sectorHistoryMetaData.dts;
        const t1: number = performance.now();
        logger.info( `Fetching ${ sectorHistoryMetaPayload.length } sector history keys took ${ t1 - t0 } ms` );

        allSectorHistoryDates = [ ...sectorHistoryMetaPayload ];
    }

    return allSectorHistoryDates;
}

export class SectorHistorySyncCoordinator {
    public async patchRepository( authToken: string ): Promise<boolean> {
        let updated = false;

        try {
            const t0: number = performance.now();

            await cache.ready();
            // Check for the next refresh interval
            const syncThresholdInMillis = await syncState.get< number >( sectorHistorySyncKey );
            if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
                const sectorHistoryDates: string[] = await fetchSectorHistoryKeys();

                if ( 0 === sectorHistoryDates.length ) {
                    const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                    await syncState.put( sectorHistorySyncKey, nextSyncThresholdInMillis );
                    return updated;
                }

                // Set throttle after successful fetch
                const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                await syncState.put( sectorHistorySyncKey, nextSyncThresholdInMillis );

                // Load items currently in cache
                const datesInCache = new Map<string, NormalizedPersistedSectorsState>();
                if ( !await sectorHistory.isEmpty() ) {
                    for ( const [ key, item ] of await sectorHistory.entries() ) {
                        if ( item?.sectors?.length > 0 && item.sectors[ 0 ].bpi.col.length > 0 ) {
                            datesInCache.set( key, item );
                        }
                    }
                }

                const missingDates: string[] = [];
                sectorHistoryDates.forEach( ( dt: string ) => {
                    if ( !datesInCache.has( dt ) ) {
                        missingDates.push( dt );
                    }
                } );

                // tmi-buu: seed the BPI status machine from the newest cached day
                // preceding the fetch window so incremental (typically single-date)
                // syncs can derive a status for their first day.
                const earliestMissingDate = [ ...missingDates ].sort()[ 0 ];
                const seedContext = earliestMissingDate
                    ? buildBpiSeedContext( datesInCache, earliestMissingDate )
                    : undefined;

                const historyItems = await refreshFromServer( authToken, missingDates, seedContext );
                if ( historyItems.length > 0 ) {
                    const validItems = historyItems.filter(
                        s => s.sectors?.length > 0 && s.sectors[ 0 ].bpi.col.length > 0
                    );

                    if ( validItems.length > 0 ) {
                        updated = true;
                        await sectorHistory.putMany( validItems.map( s => [ s.dt, s ] as const ) );
                    }

                    const t4: number = performance.now();
                    logger.info( `[ sector-history-sync-coordinator ] Persisting all missing sector history took ${ t4 - t0 } ms` );
                }
            }
        } catch ( err: any ) {
            logger.error( '[ sector-history-sync-coordinator ] Sync failed:', err );
        }

        return updated;
    }
}

const sectorAlertTypeKeyValues: readonly ( readonly [ number, string ] )[] = [
    [ SectorAlertTypes.rs_signal, 'Relative Strength Signal' ],
    [ SectorAlertTypes.rs_direction, 'Relative Strength Direction' ],
    [ SectorAlertTypes.bpi_signal, 'BPI Signal' ],
    [ SectorAlertTypes.bpi_direction, 'BPI Direction' ],
    [ SectorAlertTypes.bpi_status, 'BPI Status' ],
    [ SectorAlertTypes.wk_30_signal, '%30 Signal' ],
    [ SectorAlertTypes.wk_30_direction, '%30 Direction' ],
    [ SectorAlertTypes.wk_10_signal, '%10 Signal' ],
    [ SectorAlertTypes.wk_10_direction, '%10 Direction' ]
];
export const sectorAlertTypeKeyToDisplayValueMapper: Map<number, string> = new Map( sectorAlertTypeKeyValues );

// NOTE: in its current form, there is no "last updated" date b/c the entire history (back to 2020-01-01) is rebuilt each time it is ingested
// b/c we don't know if II has changed values or not
export async function refreshFromServer( authToken: string, missingDates: string[], seedContext?: Map< number, BpiPnfContext > ): Promise<NormalizedPersistedSectorsState[]> {
    const entities: NormalizedPersistedSectorsState[] = [];

    // Raw alerts + performer lists held back until the status pass below has
    // populated bpi.stat, keyed by `${ dt }|${ sector_id }`.
    const pendingAlerts = new Map< string, { alerts: SectorAlert[], performers: TopAndBottomPerformers } >();

    const t0: number = performance.now();
    let page: number = 0;
    const pageSize: number = 40;
    let consecutiveFailures: number = 0;
    const maxConsecutiveFailures: number = 2; // Stop after 2 consecutive failures to prevent cascading failures

    do {
        let startingIndex = page * pageSize;
        let endingIndex = startingIndex + pageSize;

        const pagedDates = [ ...missingDates.slice( startingIndex, endingIndex ) ];

        if ( 0 === pagedDates.length ) {
            break;
        }

        const response =
            await goFetch< SectorHistoryApiResponse, PersistedQueryPayload >( {
                serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
                endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
                payload: {
                    id: 'a3532ccad48cd1cd1e1ad86742cf1fdb',
                    variables: {
                        skip: 0,
                        limit: pageSize,
                        marketDataFilter: {
                            dates: pagedDates
                        }
                    }
                },
                authToken: authToken
            } );

        // If no response (API failed), increment failure counter and check if we should stop
        if (!response) {
            consecutiveFailures++;
            logger.warn(`Sector history fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

            if (consecutiveFailures >= maxConsecutiveFailures) {
                logger.warn('Too many consecutive failures, stopping sector history pagination to prevent cascading failures');
                break;
            }

            // Continue to next page to see if it's a transient issue
            page++;
            continue;
        }

        // Reset failure counter on success
        consecutiveFailures = 0;

        const historyItems = response?.data;
        if ( ( historyItems?.sectorHistoryForDates?.items?.length ?? 0 ) > 0 ) {
            let sectorEntities: NormalizedSectorMarketState[] = [];
            historyItems.sectorHistoryForDates.items
                .forEach( ( i: SectorHistoryForDate ) => {

                    const dateOfAnalysis: string = i.dt;
                    i.history.forEach( ( h: SectorStatsApiResponse ) => {
                        const performers: TopAndBottomPerformers = {
                            top_etfs: [],
                            top_stocks: [],
                            bot_etfs: [],
                            bot_stocks: [],
                        };

                        const alerts: SectorAlert[] = h.alerts?.kind.length > 0 ?
                            [ ...( h.alerts.kind.map( ( kind: number ) =>
                                ( {
                                    id: '',
                                    dt: i.dt,
                                    sector_id: -1,
                                    typeId: kind,
                                    typeName: sectorAlertTypeKeyToDisplayValueMapper.get( kind )!,
                                    bullish: false,
                                    idx: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    bpi: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        stat: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: '',
                                        stat_chg_dt: ''
                                    },
                                    p30: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    p10: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    rs: {
                                        rnk: -1,
                                        log_rtn: 0,
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    stocks: [],
                                    etfs: []
                                } )
                            ) ) ]
                            : [];

                        if ( h.alerts?.kind.length > 0 ) {
                            const sectorPerformers: SectorAlertApiResponse = h.alerts;

                            // Mapping the top stocks
                            sectorPerformers.top_stock_1 ? performers.top_stocks.push( sectorPerformers.top_stock_1 ) : '';
                            sectorPerformers.top_stock_2 ? performers.top_stocks.push( sectorPerformers.top_stock_2 ) : '';
                            sectorPerformers.top_stock_3 ? performers.top_stocks.push( sectorPerformers.top_stock_3 ) : '';

                            // Mapping the top etfs
                            sectorPerformers.top_etf_1 ? performers.top_etfs.push( sectorPerformers.top_etf_1 ) : '';
                            sectorPerformers.top_etf_2 ? performers.top_etfs.push( sectorPerformers.top_etf_2 ) : '';
                            sectorPerformers.top_etf_3 ? performers.top_etfs.push( sectorPerformers.top_etf_3 ) : '';

                            // Mapping the bottom stocks
                            sectorPerformers.bot_stock_1 ? performers.bot_stocks.push( sectorPerformers.bot_stock_1 ) : '';
                            sectorPerformers.bot_stock_2 ? performers.bot_stocks.push( sectorPerformers.bot_stock_2 ) : '';
                            sectorPerformers.bot_stock_3 ? performers.bot_stocks.push( sectorPerformers.bot_stock_3 ) : '';

                            // Mapping the top etfs
                            sectorPerformers.bot_etf_1 ? performers.bot_etfs.push( sectorPerformers.bot_etf_1 ) : '';
                            sectorPerformers.bot_etf_2 ? performers.bot_etfs.push( sectorPerformers.bot_etf_2 ) : '';
                            sectorPerformers.bot_etf_3 ? performers.bot_etfs.push( sectorPerformers.bot_etf_3 ) : '';
                        }

                        {
                            const sectorState: NormalizedSectorMarketState = {
                                dt: dateOfAnalysis,
                                sectorTmiId: h.sector_id,
                                idx: {
                                    log_rtn: h.idx_log_rtn,
                                    val: 0,
                                    delta: 0,
                                    col: normalizeColumn( h.idx_col ),
                                    col_chg_dt: h.dt_last_idx_col,
                                    sig: normalizeSignal( h.idx_sig ),
                                    sig_chg_dt: h.dt_last_idx_sig
                                },
                                bpi: {
                                    val: h.bpi_val,
                                    col: normalizeColumn( h.bpi_col ),
                                    col_chg_dt: h.dt_last_bpi_col,
                                    sig: normalizeSignal( h.bpi_sig ),
                                    sig_chg_dt: h.dt_last_bpi_sig,
                                    stat: null,
                                    stat_chg_dt: ''
                                },
                                p30: {
                                    val: h.p30_val,
                                    col: normalizeColumn( h.p30_col ),
                                    col_chg_dt: h.dt_last_p30_col,
                                    sig: normalizeSignal( h.p30_sig ),
                                    sig_chg_dt: h.dt_last_p30_sig
                                },
                                p10: {
                                    val: h.p10_val,
                                    col: normalizeColumn( h.p10_col ),
                                    col_chg_dt: h.dt_last_p10_col,
                                    sig: normalizeSignal( h.p10_sig ),
                                    sig_chg_dt: ''
                                },
                                rs: {
                                    rnk: h.rs_rank,
                                    log_rtn: h.rs_log_rtn,
                                    val: 0,
                                    col: normalizeColumn( h.rs_col ),
                                    col_chg_dt: h.rs_chg_dt,
                                    sig: normalizeSignal( h.rs_sig ),
                                    sig_chg_dt: h.dt_last_rs_sig
                                },
                                alerts: []
                            };

                            // tmi-buu: composition is deferred until bpi.stat has been
                            // derived (chronological pass below) — composing here decided
                            // the best/worst performer lists against a stat that is
                            // always null at this point.
                            if ( alerts.length > 0 ) {
                                pendingAlerts.set( `${ dateOfAnalysis }|${ h.sector_id }`, { alerts, performers } );
                            }

                            sectorEntities.push( sectorState );
                        }
                    } );

                    entities.push( {
                        dt: dateOfAnalysis,
                        sectors: [ ...sectorEntities ]
                    } );

                    // Clear the list of sector data
                    sectorEntities = [];
                } );

            const t1: number = performance.now();
            logger.info( `Fetching & mapping sector market data took ${ t1 - t0 } ms` );
        }

        // update the page #
        page++;
    } while ( page * pageSize < missingDates.length );

    rehydrateFromLogReturns( entities );

    // tmi-buu: chronological BPI status pass (rehydrateFromLogReturns just
    // sorted `entities` date-ascending), then alert composition against the
    // populated state. The bullish/bearish choice for a BPI Status Change
    // alert — which selects best vs. worst performer lists — needs the very
    // status this pass derives.
    const bpiCtx = new Map< number, BpiPnfContext >( seedContext ?? [] );
    for ( const state of entities ) {
        for ( const sectorState of state.sectors ) {
            const derived = deriveBpiStatus(
                bpiCtx.get( sectorState.sectorTmiId ) ?? null,
                sectorState.bpi.col?.toLocaleLowerCase(),
                sectorState.bpi.sig?.toLocaleLowerCase(),
                sectorState.bpi.val
            );
            sectorState.bpi.stat = derived.stat;
            if ( derived.nextCtx ) {
                bpiCtx.set( sectorState.sectorTmiId, derived.nextCtx );
            }

            const pending = pendingAlerts.get( `${ state.dt }|${ sectorState.sectorTmiId }` );
            if ( pending ) {
                sectorState.alerts = [ ...composeSectorAlertsForDate( sectorState, pending.alerts, pending.performers ) ];
            }
        }
    }

    return entities;
}

const LOG_RETURN_BASE = 1000;

/**
 * Rehydrates stable cumulative values from daily log returns for EW Index and RS.
 * Log returns are additive: val_t = BASE * e^(Σ log_rtn from start to t).
 * Delta is the difference between consecutive rehydrated values.
 * Mutates data in place. Sorts chronologically to ensure correct accumulation order.
 */
export function rehydrateFromLogReturns( data: NormalizedPersistedSectorsState[] ): void {
    if ( data.length === 0 ) return;

    data.sort( ( a, b ) => a.dt.localeCompare( b.dt ) );

    const idxRunningSum = new Map< number, number >();
    const idxPreviousVal = new Map< number, number >();
    const rsRunningSum = new Map< number, number >();

    for ( const state of data ) {
        for ( const sectorState of state.sectors ) {
            const sectorId = sectorState.sectorTmiId;

            // EW Index rehydration
            if ( sectorState.idx ) {
                const sum = ( idxRunningSum.get( sectorId ) ?? 0 ) + sectorState.idx.log_rtn;
                idxRunningSum.set( sectorId, sum );

                const val = LOG_RETURN_BASE * Math.exp( sum );
                const prevVal = idxPreviousVal.get( sectorId );

                sectorState.idx.val = val;
                sectorState.idx.delta = prevVal != null ? val - prevVal : undefined;
                idxPreviousVal.set( sectorId, val );
            }

            // RS rehydration
            const rsSum = ( rsRunningSum.get( sectorId ) ?? 0 ) + sectorState.rs.log_rtn;
            rsRunningSum.set( sectorId, rsSum );
            sectorState.rs.val = LOG_RETURN_BASE * Math.exp( rsSum );
        }
    }
}

export function hydrate( entity: NormalizedPersistedSectorsState, ctx: CompositionContext ): Tuple<string, Map<number, SectorMarketState>> {
    const dateOfAnalysis: string = entity.dt;

    const sectorToStateMap = new Map<number, SectorMarketState>();
    for ( const sectorState of entity.sectors ) {
        const sector = ctx.getSectorByTmiId( sectorState.sectorTmiId );
        if ( typeof sector !== 'undefined' ) {
            sectorToStateMap.set( sectorState.sectorTmiId, {
                dt: dateOfAnalysis,
                sector: sector,
                idx: sectorState.idx,
                bpi: sectorState.bpi,
                p30: sectorState.p30,
                p10: sectorState.p10,
                rs: sectorState.rs,
                alerts: sectorState.alerts
            } );
        } else {
            logger.warn( `The given TMI ID (${ sectorState.sectorTmiId }) did not map to a valid sector object.` );
        }
    }

    return {
        item1: dateOfAnalysis,
        item2: sectorToStateMap
    }
}

type TopAndBottomPerformers = {
    top_stocks: string[],
    bot_stocks: string[],
    top_etfs: string[],
    bot_etfs: string[]
}

export function composeSectorAlertsForDate( eodState: Readonly< NormalizedSectorMarketState >, alerts: SectorAlert[], performers: TopAndBottomPerformers ): Readonly<SectorAlert>[] {
    const workingSectorAlertList: Readonly< SectorAlert >[] = [];
    for ( const alert of alerts ) {
        const alertKey = `${ alert.dt }+${ eodState.sectorTmiId }+${ alert.typeId }`;

        let bullish: boolean = false;
        switch ( alert.typeId ) {
            case SectorAlertTypes.rs_signal:
                bullish = isBullishSignal( eodState.rs.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.rs_direction:
                bullish = isBullishDirection( eodState.rs.col.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.bpi_signal:
                bullish = isBullishSignal( eodState.bpi.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.bpi_direction:
                bullish = isBullishDirection( eodState.bpi.col.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.bpi_status:
                bullish = isBullishStatus( eodState.bpi.stat?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_30_signal:
                bullish = isBullishSignal( eodState.p30.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_30_direction:
                bullish = isBullishDirection( eodState.p30.col.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_10_signal:
                bullish = isBullishSignal( eodState.p10.sig?.toLowerCase() ?? '' );
                break;
            case SectorAlertTypes.wk_10_direction:
                bullish = isBullishDirection( eodState.p10.col.toLowerCase() ?? '' );
                break;
        }

        // Build the Sector Alert
        workingSectorAlertList.push( {
            id: alertKey,
            dt: alert.dt,
            sector_id: eodState.sectorTmiId,
            typeId: alert.typeId,
            typeName: alert.typeName,
            bullish: bullish,
            bpi: eodState.bpi,
            p30: eodState.p30,
            p10: eodState.p10,
            rs: eodState.rs,
            stocks: bullish ? performers.top_stocks : performers.bot_stocks,
            etfs: bullish ? performers.top_etfs : performers.bot_etfs
        } );
    }

    return workingSectorAlertList;
}

export function isBullishSignal( signal: string ): boolean {
    switch ( signal ) {
        case 'buy':
            return true;
        case 'sell':
        default:
            return false
    }
}

export function isBullishDirection( direction: string ): boolean {
    switch ( direction ) {
        case 'x':
            return true;
        case 'o':
        default:
            return false
    }
}

const OverboughtThreshold = 70.0;
const OversoldThreshold = 30.0;

export type BpiPnfContext = {
    previousFieldPosition: number,
    previousColumn: string,
    previousSignal: string
};

/**
 * tmi-buu: the P&F BPI status machine as a pure fold step. `prevCtx` is the
 * previous trading day's (field position, column, signal) for the sector —
 * null when no prior day is known, in which case the row can only seed
 * context. Column/signal are expected lowercase ('x'/'o', 'buy'/'sell');
 * '---' (WMD-1251 neutral) bails to a null status without touching context.
 */
export function deriveBpiStatus(
    prevCtx: BpiPnfContext | null,
    column: string | null | undefined,
    signal: string | null | undefined,
    fieldPosition: number
): { stat: string | null, nextCtx: BpiPnfContext | null } {
    if ( column === '---' || signal === '---' ) {
        return { stat: null, nextCtx: prevCtx };
    }

    const nextCtx = ( fieldPosition && column && signal )
        ? { previousFieldPosition: fieldPosition, previousColumn: column, previousSignal: signal }
        : prevCtx;

    if ( !prevCtx ) {
        return { stat: null, nextCtx: nextCtx === prevCtx ? null : nextCtx };
    }

    let stat: string | null = null;
    if ( fieldPosition > 0.0 && column && signal ) {
        if ( isBullishSignal( signal ) ) {
            if ( !isBullishDirection( column ) ) {
                if ( prevCtx.previousFieldPosition > OverboughtThreshold && fieldPosition < OverboughtThreshold ) {
                    stat = BpiStatus.BearAlert;
                } else if ( fieldPosition > OverboughtThreshold ) {
                    stat = BpiStatus.BullTop;
                } else {
                    stat = BpiStatus.BullCorrection;
                }
            } else {
                stat = BpiStatus.BullConfirmed;
            }
        } else {
            if ( isBullishDirection( column ) ) {
                if ( prevCtx.previousFieldPosition < OversoldThreshold && fieldPosition > OversoldThreshold ) {
                    stat = BpiStatus.BullAlert;
                } else if ( fieldPosition < OverboughtThreshold ) {
                    stat = BpiStatus.BearCorrection;
                } else {
                    stat = BpiStatus.BullConfirmed;
                }
            } else {
                stat = BpiStatus.BearConfirmed;
            }
        }
    }

    return { stat, nextCtx };
}

/**
 * tmi-buu: builds the status machine's seed for an incremental sync — the BPI
 * (field position, column, signal) of each sector on the latest cached date
 * strictly before `earliestMissingDate`. Empty on a cold cache, in which case
 * the chronological pass self-seeds after each sector's first fetched day.
 */
export function buildBpiSeedContext(
    datesInCache: Map< string, NormalizedPersistedSectorsState >,
    earliestMissingDate: string
): Map< number, BpiPnfContext > {
    const seed = new Map< number, BpiPnfContext >();

    const seedDate = [ ...datesInCache.keys() ]
        .filter( dt => dt < earliestMissingDate )
        .sort( ( d1, d2 ) => d2.localeCompare( d1 ) )[ 0 ];
    if ( !seedDate ) {
        return seed;
    }

    for ( const sectorState of datesInCache.get( seedDate )!.sectors ) {
        const { val, col, sig } = sectorState.bpi;
        if ( val && col && sig ) {
            seed.set( sectorState.sectorTmiId, {
                previousFieldPosition: val,
                previousColumn: col.toLocaleLowerCase(),
                previousSignal: sig.toLocaleLowerCase()
            } );
        }
    }

    return seed;
}

// P&F BPI status convention: a "Bear Correction" is a counter-trend rally within a
// bear market (chart on a P&F sell signal but rising in X's) — the CHANGE into it is
// bullish. Symmetrically, a "Bull Correction" is a pullback within a bull market
// (buy signal, falling in O's) — the change into it is bearish.
function isBullishStatus( status: string ): boolean {
    switch ( status ) {
        case 'bull confirmed':
        case 'bull alert':
        case 'bear correction':
            return true;
        case 'bear confirmed':
        case 'bull correction':
        case 'bull top':
        case 'bear alert':
        default:
            return false;
    }
}